import { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError, fileUrl, getStoredToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import TopNav from "@/components/TopNav";
import ConnectionMap from "@/components/ConnectionMap";
import OrgChart from "@/components/OrgChart";
import PhotoUpload from "@/components/PhotoUpload";
import OnboardingTour from "@/components/OnboardingTour";
import MemberDetailDialog from "@/components/MemberDetailDialog";
import CircleSettingsDialog from "@/components/CircleSettingsDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Users, TreeStructure, Megaphone, ChatsCircle, Image as ImageIcon,
  CheckCircle, Hourglass, Copy, Plus, Crown, ShieldCheck, Link as LinkIcon, UserPlus, Sparkle, Flower, Gear, PencilSimple, CreditCard, HandPointing, File, Trash, DownloadSimple,
} from "@phosphor-icons/react";

const REL_TYPES = [
  { value: "parent", label: "Parent" },
  { value: "child", label: "Child" },
  { value: "sibling", label: "Sibling" },
  { value: "spouse", label: "Spouse" },
  { value: "grandparent", label: "Grandparent" },
  { value: "grandchild", label: "Grandchild" },
];

function MemberAvatar({ member, size = 40 }) {
  const initial = member?.name?.charAt(0).toUpperCase() || "?";
  return (
    <Avatar style={{ width: size, height: size }} className="border border-sage">
      {member?.profile_photo_path && <AvatarImage src={fileUrl(member.profile_photo_path)} />}
      <AvatarFallback className="bg-forest text-ivory">{initial}</AvatarFallback>
    </Avatar>
  );
}

export default function GroupPage() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [group, setGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [relationships, setRelationships] = useState([]);
  const [connections, setConnections] = useState([]);
  const [graphData, setGraphData] = useState({ nodes: [], edges: [] });
  const [invites, setInvites] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [profile, setProfile] = useState(null);
  const [memorials, setMemorials] = useState([]);
  const [tab, setTab] = useState("map");
  const [settingsOpen, setSettingsOpen] = useState(false);

  const isAdmin = group?.role === "owner" || group?.role === "admin";
  const isOwner = group?.role === "owner";
  const isVerified = group?.status === "verified";
  const structure = group?.structure || "flat";
  const profileLoaded = profile !== null;
  const needsOnboarding = isVerified && profileLoaded && !profile?.completed_at;

  const loadAll = async () => {
    try {
      const [g, m, rels, anns, ph, mems] = await Promise.all([
        api.get(`/groups/${groupId}`),
        api.get(`/groups/${groupId}/members`),
        api.get(`/groups/${groupId}/relationships`),
        api.get(`/groups/${groupId}/announcements`),
        api.get(`/groups/${groupId}/photos`),
        api.get(`/groups/${groupId}/memorials`),
      ]);
      setGroup(g.data);
      setMembers(m.data);
      setRelationships(rels.data);
      setAnnouncements(anns.data);
      setPhotos(ph.data);
      setMemorials(mems.data);
    } catch (err) {
      console.warn("Failed to load group:", err);
      if (err?.response?.status !== 401) {
        toast.error(formatApiError(err) || "Couldn't load this circle");
      }
    }
  };

  const loadConnections = async () => {
    try {
      const [c, gd] = await Promise.all([
        api.get(`/groups/${groupId}/connections`),
        api.get(`/groups/${groupId}/graph`),
      ]);
      setConnections(c.data);
      setGraphData(gd.data);
    } catch (err) {
      // Pending members get 403 from these endpoints — that's expected.
      // Anything else is worth logging.
      if (err?.response?.status !== 403) {
        console.warn("Failed to load connections/graph:", err);
      }
    }
  };

  const loadInvites = async () => {
    try {
      const { data } = await api.get(`/groups/${groupId}/invites`);
      setInvites(data);
    } catch (err) {
      // Non-admins get 403 — expected. Log anything else.
      if (err?.response?.status !== 403) {
        console.warn("Failed to load invites:", err);
      }
    }
  };

  useEffect(() => {
    loadAll();
  }, [groupId]);

  useEffect(() => {
    if (isVerified) loadConnections();
    if (isAdmin) loadInvites();
    if (isVerified) loadProfile();
  }, [isVerified, isAdmin, members.length, relationships.length]);

  const loadProfile = async () => {
    try {
      const { data } = await api.get(`/groups/${groupId}/onboarding`);
      setProfile(data || { completed_at: null });
    } catch (err) {
      console.warn("Failed to load onboarding profile:", err);
    }
  };

  // Auto-redirect to onboarding ONCE for newly approved members (non-owner)
  // who haven't filled in their profile. Skipped for owners — they see the
  // banner instead and can dismiss it.
  useEffect(() => {
    if (!needsOnboarding) return;
    if (group?.role === "owner") return;
    const key = `legacylink:onboarded-prompt:${groupId}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    navigate(`/groups/${groupId}/onboarding`, { replace: true });
  }, [needsOnboarding, group?.role, groupId, navigate]);

  if (!group) return <div className="min-h-screen bg-ivory"><TopNav /><div className="p-10 text-sageGray">Loading…</div></div>;

  return (
    <div className="min-h-screen bg-ivory grain-bg">
      <TopNav />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <button onClick={() => navigate("/dashboard")} className="text-sm text-sageGray hover:text-forest transition-colors mb-3" data-testid="back-to-dashboard">← All circles</button>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-display text-4xl md:text-5xl text-forest" data-testid="group-name">{group.name}</h1>
              {isAdmin && (
                <button
                  type="button"
                  onClick={() => setSettingsOpen(true)}
                  className="p-2 rounded-full text-forest/70 hover:text-forest hover:bg-cream transition-colors"
                  title="Circle settings"
                  data-testid="open-circle-settings"
                >
                  <Gear size={22} weight="duotone" />
                </button>
              )}
            </div>
            {group.description && <p className="text-sageGray mt-2 max-w-2xl">{group.description}</p>}
            <div className="flex gap-2 mt-3">
              {group.role === "owner" && <Badge className="bg-gold/20 text-forest border-0 rounded-full"><Crown size={12} className="mr-1" weight="fill" /> owner</Badge>}
              {group.role === "admin" && <Badge className="bg-forest/10 text-forest border-0 rounded-full"><ShieldCheck size={12} className="mr-1" weight="fill" /> admin</Badge>}
              <Badge className={`${isVerified ? "bg-forest/10 text-forest" : "bg-gold/20 text-forest"} border-0 rounded-full`}>
                {isVerified ? <CheckCircle size={12} className="mr-1" weight="fill" /> : <Hourglass size={12} className="mr-1" weight="fill" />}
                {group.status}
              </Badge>
            </div>
          </div>
        </div>

        <div className="gold-divider mb-8" />

        {group.subscription_status && !["trialing", "active"].includes(group.subscription_status) && (
          <div className="legacy-card border-red-300 bg-red-50/60 p-5 mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="circle-paused-banner">
            <span className="w-11 h-11 rounded-full bg-red-600 text-white flex items-center justify-center shrink-0">
              <CreditCard size={20} weight="duotone" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="font-display text-lg text-red-800">This circle is paused</p>
              <p className="text-sm text-red-700/90">
                {group.role === "owner"
                  ? "Your payment couldn't be collected. New members and messages are disabled until billing is resolved."
                  : "Billing needs attention from the circle owner. You can still view the map; inviting members and messaging is paused."}
              </p>
            </div>
            {group.role === "owner" && (
              <Button onClick={() => setSettingsOpen(true)} className="bg-red-600 hover:bg-red-700 text-white rounded-full shrink-0" data-testid="paused-fix-button">
                Fix billing
              </Button>
            )}
          </div>
        )}

        {isVerified && needsOnboarding && (
          <div className="legacy-card border-gold/60 bg-gradient-to-br from-white to-cream/40 p-5 mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="onboarding-banner">
            <span className="w-11 h-11 rounded-full bg-forest text-gold flex items-center justify-center shrink-0">
              <Sparkle size={20} weight="duotone" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="font-display text-lg text-forest">Complete your family info</p>
              <p className="text-sm text-sageGray">Add your parents, siblings, spouse and children. We'll auto-link anyone already in the circle.</p>
            </div>
            <Button onClick={() => navigate(`/groups/${groupId}/onboarding`)} className="bg-forest hover:bg-forestSoft text-ivory rounded-full shrink-0" data-testid="open-onboarding-button">
              Open onboarding
            </Button>
          </div>
        )}

        {!isVerified ? (
          <div className="legacy-card p-10 text-center max-w-2xl mx-auto" data-testid="pending-state">
            <div className="w-16 h-16 mx-auto rounded-full bg-gold/20 text-forest flex items-center justify-center mb-4">
              <Hourglass size={28} weight="duotone" />
            </div>
            <h3 className="font-display text-2xl text-forest mb-2">Awaiting approval</h3>
            <p className="text-sageGray">Your request to join is pending approval from an admin of this circle. You'll get access to the family map and members once verified.</p>
          </div>
        ) : (
          <Tabs value={tab} onValueChange={setTab} className="w-full">
            {!isAdmin && group?.role !== "branch_leader" && (
              <OnboardingTour
                groupId={groupId}
                groupName={group?.name}
                userId={user?.id}
                onTabChange={setTab}
              />
            )}
            <TabsList className="bg-white border border-sage h-auto p-1.5 flex flex-wrap gap-1 rounded-xl" data-testid="group-tabs">
              <TabsTrigger value="map" className="data-[state=active]:bg-forest data-[state=active]:text-ivory rounded-lg px-4 py-2" data-testid="tab-map"><TreeStructure size={16} className="mr-2" />Map</TabsTrigger>
              <TabsTrigger value="members" className="data-[state=active]:bg-forest data-[state=active]:text-ivory rounded-lg px-4 py-2" data-testid="tab-members"><Users size={16} className="mr-2" />Members</TabsTrigger>
              <TabsTrigger value="relationships" className="data-[state=active]:bg-forest data-[state=active]:text-ivory rounded-lg px-4 py-2" data-testid="tab-relationships"><TreeStructure size={16} className="mr-2" />Relationships</TabsTrigger>
              <TabsTrigger value="announcements" className="data-[state=active]:bg-forest data-[state=active]:text-ivory rounded-lg px-4 py-2" data-testid="tab-announcements"><Megaphone size={16} className="mr-2" />Announcements</TabsTrigger>
              <TabsTrigger value="messages" className="data-[state=active]:bg-forest data-[state=active]:text-ivory rounded-lg px-4 py-2" data-testid="tab-messages"><ChatsCircle size={16} className="mr-2" />Messages</TabsTrigger>
              <TabsTrigger value="photos" className="data-[state=active]:bg-forest data-[state=active]:text-ivory rounded-lg px-4 py-2" data-testid="tab-photos"><ImageIcon size={16} className="mr-2" />Photos</TabsTrigger>
              <TabsTrigger value="documents" className="data-[state=active]:bg-forest data-[state=active]:text-ivory rounded-lg px-4 py-2" data-testid="tab-documents"><File size={16} className="mr-2" />Documents</TabsTrigger>
              <TabsTrigger value="memorials" className="data-[state=active]:bg-forest data-[state=active]:text-ivory rounded-lg px-4 py-2" data-testid="tab-memorials"><Flower size={16} className="mr-2" />Memorials</TabsTrigger>
            </TabsList>

            <TabsContent value="map" className="mt-6">
              <MapTab
                groupId={groupId}
                graphData={graphData}
                connections={connections}
                invites={invites}
                isAdmin={isAdmin}
                isOwner={isOwner}
                currentUser={user}
                structure={structure}
                profileCompleted={!!profile?.completed_at}
                onGoToMembers={() => setTab("members")}
                onEditFamilyInfo={() => navigate(`/groups/${groupId}/onboarding`)}
                onClaimed={async () => { await loadAll(); await loadConnections(); }}
              />
            </TabsContent>
            <TabsContent value="members" className="mt-6">
              <MembersTab groupId={groupId} group={group} members={members} invites={invites} reload={async () => { await loadAll(); await loadInvites(); }} />
            </TabsContent>
            <TabsContent value="relationships" className="mt-6">
              <RelationshipsTab groupId={groupId} members={members} relationships={relationships} reload={async () => { await loadAll(); await loadConnections(); }} currentUser={user} />
            </TabsContent>
            <TabsContent value="announcements" className="mt-6">
              <AnnouncementsTab groupId={groupId} announcements={announcements} isAdmin={isAdmin} reload={loadAll} members={members} />
            </TabsContent>
            <TabsContent value="messages" className="mt-6">
              <MessagesTab members={members} currentUser={user} />
            </TabsContent>
            <TabsContent value="photos" className="mt-6">
              <PhotosTab groupId={groupId} photos={photos} members={members} reload={loadAll} />
            </TabsContent>
            <TabsContent value="documents" className="mt-6">
              <DocumentsTab groupId={groupId} currentUser={user} isAdmin={isAdmin} />
            </TabsContent>
            <TabsContent value="memorials" className="mt-6">
              <MemorialsTab groupId={groupId} memorials={memorials} members={members} currentUser={user} reload={loadAll} />
            </TabsContent>
          </Tabs>
        )}
      </main>
      <CircleSettingsDialog group={group} open={settingsOpen} onOpenChange={setSettingsOpen} onSaved={async () => { await loadAll(); await loadConnections(); }} members={members} />
    </div>
  );
}

/* ---------- MAP TAB ---------- */
function MapTab({ groupId, graphData, connections, invites, isAdmin, isOwner, currentUser, structure, profileCompleted = false, onGoToMembers, onEditFamilyInfo, onClaimed }) {
  const [inviteOpen, setInviteOpen] = useState(false);
  const [mapLayout, setMapLayout] = useState("web"); // "web" | "tree"
  const [activeNode, setActiveNode] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [approving, setApproving] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(() => {
    try {
      return localStorage.getItem(`legacylink:findme-banner:${groupId}:${currentUser?.id}`) === "1";
    } catch {
      return false;
    }
  });
  const activeInvite = invites && invites.length > 0 ? invites[0] : null;
  const memberCount = graphData?.nodes?.length || 0;
  const showInviteCTA = isAdmin && memberCount <= 1;
  const inviteLink = activeInvite ? `${window.location.origin}/join/${activeInvite.code}` : "";

  const isHierarchy = structure === "hierarchy";
  const hasUnclaimedPlaceholders = (graphData?.nodes || []).some((n) => (n.is_placeholder || n.is_living) && !n.is_claimed);
  // The "Find yourself" banner only shows to non-owners with unclaimed seats to scan.
  const showFindYourself = !bannerDismissed && hasUnclaimedPlaceholders && !isOwner;

  const dismissBanner = () => {
    setBannerDismissed(true);
    try { localStorage.setItem(`legacylink:findme-banner:${groupId}:${currentUser?.id}`, "1"); } catch { /* ignore */ }
  };

  const claimNode = async (node) => {
    if (!node) return;
    setClaiming(true);
    try {
      await api.post(`/groups/${groupId}/placeholders/${node.id}/claim`);
      toast.success("Spot claimed — your photo now sits in this position.");
      setActiveNode(null);
      dismissBanner();
      await onClaimed?.();
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't claim this spot");
    } finally {
      setClaiming(false);
    }
  };

  const confirmNode = async (node) => {
    if (!node) return;
    setConfirming(true);
    try {
      const { data } = await api.post(`/groups/${groupId}/placeholders/${node.id}/confirm`);
      toast.success(data.confirmed ? "Verified by family." : `Confirmed (${data.count}/5)`);
      await onClaimed?.();
      // Re-pull node from refreshed graph so progress UI updates in-place.
      setActiveNode((curr) => curr ? { ...curr, confirmations_count: data.count, is_community_verified: !!data.confirmed } : curr);
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't confirm");
    } finally {
      setConfirming(false);
    }
  };

  const approveNode = async (node) => {
    if (!node) return;
    setApproving(true);
    try {
      await api.post(`/groups/${groupId}/placeholders/${node.id}/approve`);
      toast.success("Placeholder approved — now visible to everyone.");
      setActiveNode(null);
      await onClaimed?.();
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't approve placeholder");
    } finally {
      setApproving(false);
    }
  };

  const rejectNode = async (node) => {
    if (!node) return;
    if (!window.confirm("Reject and delete this placeholder? This can't be undone.")) return;
    setApproving(true);
    try {
      await api.post(`/groups/${groupId}/placeholders/${node.id}/reject`);
      toast.success("Placeholder rejected.");
      setActiveNode(null);
      await onClaimed?.();
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't reject placeholder");
    } finally {
      setApproving(false);
    }
  };

  // Fix 17 — inline edit a placeholder. Returns false on failure so the
  // dialog keeps the edit form open for the user to retry.
  const editNode = async (node, patch) => {
    if (!node) return false;
    if (!patch || Object.keys(patch).length === 0) {
      return true; // nothing to save
    }
    setSavingEdit(true);
    try {
      const { data } = await api.patch(`/groups/${groupId}/placeholders/${node.id}`, patch);
      toast.success("Placeholder updated.");
      // Merge the new fields locally so the dialog reflects the change
      // immediately, then reload the graph so the tree picks up new labels.
      setActiveNode((curr) => curr ? { ...curr, ...data, role_or_relationship: data?.role_or_relationship ?? curr.role_or_relationship } : curr);
      await onClaimed?.();
      return true;
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't save changes");
      return false;
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-4">
        {showFindYourself && (
          <div className="legacy-card p-4 border-2 border-dashed border-[#E89C3F]/70 bg-[#FFF8EC] flex items-start gap-3" data-testid="findme-banner">
            <span className="w-10 h-10 shrink-0 rounded-full bg-[#E89C3F]/20 text-[#8A5A1F] flex items-center justify-center">
              <HandPointing size={20} weight="duotone" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="font-display text-forest text-base">Find yourself on the {isHierarchy ? "chart" : "family tree"}</p>
              <p className="text-sm text-sageGray mt-0.5">Click your name on a dashed amber seat to claim your spot. Connections drawn through that placeholder will transfer to you.</p>
            </div>
            <button onClick={dismissBanner} className="text-sageGray hover:text-forest text-xs underline shrink-0" data-testid="findme-banner-dismiss">Dismiss</button>
          </div>
        )}
        {showInviteCTA && (
          <div className="legacy-card p-6 border-gold/60 bg-gradient-to-br from-white to-cream/40 flex flex-col md:flex-row items-start md:items-center gap-4" data-testid="map-invite-cta">
            <span className="w-12 h-12 shrink-0 rounded-full bg-forest text-gold flex items-center justify-center">
              <UserPlus size={22} weight="duotone" />
            </span>
            <div className="flex-1 min-w-0">
              <h4 className="font-display text-xl text-forest">Your map is just you — for now.</h4>
              <p className="text-sm text-sageGray mt-1">Invite a relative to start building the family connection map.</p>
            </div>
            <Button
              onClick={() => setInviteOpen(true)}
              className="bg-forest hover:bg-forestSoft text-ivory rounded-full px-5 shrink-0"
              data-testid="map-invite-button"
            >
              <UserPlus size={16} className="mr-1.5" weight="bold" /> Invite a relative
            </Button>
          </div>
        )}
        {!isHierarchy && (
          <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="map-header-row">
            {profileCompleted && onEditFamilyInfo ? (
              <button
                type="button"
                onClick={onEditFamilyInfo}
                className="text-xs uppercase tracking-widest text-forest/80 hover:text-forest underline decoration-gold/60 underline-offset-4 transition-colors"
                data-testid="edit-family-info-button"
              >
                <PencilSimple size={12} className="inline mr-1" weight="bold" /> Edit my family info
              </button>
            ) : <span />}
            <div className="flex items-center justify-end gap-1 p-1 bg-white border border-sage rounded-full w-fit" data-testid="map-layout-toggle">
              <button
                type="button"
                onClick={() => setMapLayout("web")}
                className={`text-xs uppercase tracking-widest px-4 py-1.5 rounded-full transition-colors ${mapLayout === "web" ? "bg-forest text-ivory" : "text-sageGray hover:text-forest"}`}
                data-testid="map-layout-web"
              >Web</button>
              <button
                type="button"
                onClick={() => setMapLayout("tree")}
                className={`text-xs uppercase tracking-widest px-4 py-1.5 rounded-full transition-colors ${mapLayout === "tree" ? "bg-forest text-ivory" : "text-sageGray hover:text-forest"}`}
                data-testid="map-layout-tree"
              >Tree</button>
            </div>
          </div>
        )}
        {isHierarchy ? (
          <OrgChart
            nodes={graphData?.nodes || []}
            ownerControls={false}
            onSelect={setActiveNode}
            onClaim={(node) => setActiveNode(node)}
          />
        ) : (
          <ConnectionMap data={graphData} layout={mapLayout} onSelect={setActiveNode} />
        )}
      </div>

      {isOwner && (graphData?.unconnected_members?.length || 0) > 0 && (
        <div className="legacy-card p-6 mt-6" data-testid="awaiting-connection-panel">
          <div className="flex items-start gap-3 mb-3">
            <span className="w-9 h-9 rounded-lg bg-[#FFF6E5] text-[#8A5A1F] flex items-center justify-center shrink-0">
              <UserPlus size={20} weight="duotone" />
            </span>
            <div>
              <h3 className="font-display text-xl text-forest leading-tight">Awaiting Family Connection</h3>
              <p className="text-sm text-sageGray mt-0.5">
                These members joined but the family chain back to you hasn't been drawn yet. Add their relatives to the tree to connect them.
              </p>
            </div>
          </div>
          <ul className="divide-y divide-sage/40" data-testid="awaiting-connection-list">
            {graphData.unconnected_members.map((m) => (
              <li key={m.id} className="py-3 flex items-center gap-3" data-testid={`awaiting-connection-${m.id}`}>
                <Avatar className="w-10 h-10 border border-sage shrink-0">
                  {m.profile_photo_path && <AvatarImage src={fileUrl(m.profile_photo_path)} alt={m.name} />}
                  <AvatarFallback className="bg-forest text-ivory text-base">{(m.name || "?").charAt(0).toUpperCase()}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-forest font-medium truncate">{m.name}</p>
                  <p className="text-xs text-sageGray">
                    Has joined but their family connection hasn't been established yet. Add their relatives to the tree to connect them.
                  </p>
                </div>
                {m.branch && (
                  <Badge className="bg-cream text-sageGray border border-sage rounded-full text-[10px] uppercase tracking-widest shrink-0">{m.branch} side</Badge>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="bg-white" data-testid="map-invite-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl text-forest">Invite a relative</DialogTitle></DialogHeader>
          {activeInvite ? (
            <div className="space-y-5 pt-2">
              <div>
                <p className="text-xs uppercase tracking-widest text-sageGray mb-2">Share this link</p>
                <div className="flex items-center gap-2 p-3 bg-cream border border-sage rounded-lg">
                  <code className="flex-1 text-sm text-forest break-all" data-testid="map-invite-link">{inviteLink}</code>
                  <Button
                    size="sm"
                    onClick={() => { navigator.clipboard.writeText(inviteLink); toast.success("Invite link copied"); }}
                    className="bg-forest hover:bg-forestSoft text-ivory rounded-full"
                    data-testid="map-copy-link-button"
                  ><LinkIcon size={14} className="mr-1" weight="bold" /> Copy</Button>
                </div>
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-sageGray mb-2">Or share the code</p>
                <div className="flex items-center gap-2 p-3 bg-cream border border-sage rounded-lg">
                  <code className="flex-1 font-mono tracking-widest text-forest" data-testid="map-invite-code">{activeInvite.code}</code>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { navigator.clipboard.writeText(activeInvite.code); toast.success("Code copied"); }}
                    className="border-forest/30 text-forest hover:bg-white rounded-full"
                    data-testid="map-copy-code-button"
                  ><Copy size={14} className="mr-1" /> Copy</Button>
                </div>
              </div>
              <p className="text-xs text-sageGray text-center">Anyone with this link can request to join. You'll approve them in the <button onClick={() => { setInviteOpen(false); onGoToMembers(); }} className="underline decoration-gold underline-offset-4 text-forest" data-testid="map-goto-members">Members tab</button>.</p>
            </div>
          ) : (
            <div className="space-y-4 pt-2 text-center">
              <p className="text-sageGray">No active invite codes yet.</p>
              <Button onClick={() => { setInviteOpen(false); onGoToMembers(); }} className="bg-forest hover:bg-forestSoft text-ivory rounded-full" data-testid="map-create-invite-redirect">Create one in the Members tab</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <MemberDetailDialog
        member={activeNode}
        open={!!activeNode}
        onOpenChange={(o) => !o && setActiveNode(null)}
        onClaim={claimNode}
        claiming={claiming}
        onConfirm={confirmNode}
        confirming={confirming}
        onApprove={approveNode}
        onReject={rejectNode}
        approving={approving}
        onEdit={editNode}
        savingEdit={savingEdit}
        currentUserId={currentUser?.id}
        isOwner={isOwner}
        isAdmin={isAdmin}
      />
    </div>
  );
}

/* ---------- MEMBERS TAB ---------- */
function MembersTab({ groupId, group, members, invites, reload }) {
  const { user } = useAuth();
  const isOwner = group.role === "owner";
  const isAdmin = group.role === "owner" || group.role === "admin";
  const pending = members.filter(m => m.status === "pending");
  const [autoApprove, setAutoApprove] = useState(!!group.auto_approve);
  const [activeMember, setActiveMember] = useState(null);

  useEffect(() => {
    setAutoApprove(!!group.auto_approve);
  }, [group.auto_approve]);

  const toggleAutoApprove = async (next) => {
    setAutoApprove(next);
    try {
      await api.patch(`/groups/${groupId}/settings`, { auto_approve: next });
      toast.success(next ? "Auto-approve turned on" : "Auto-approve turned off");
      reload();
    } catch (err) {
      setAutoApprove(!next);
      toast.error(formatApiError(err) || "Couldn't update setting");
    }
  };

  const approve = async (uid) => {
    try {
      await api.post(`/groups/${groupId}/members/${uid}/approve`);
      toast.success("Approved");
      reload();
    } catch (err) {
      console.warn("Approve failed:", err);
      toast.error(formatApiError(err) || "Couldn't approve this member");
    }
  };
  const reject = async (uid) => {
    try {
      await api.post(`/groups/${groupId}/members/${uid}/reject`);
      toast.success("Removed");
      reload();
    } catch (err) {
      console.warn("Reject failed:", err);
      toast.error(formatApiError(err) || "Couldn't remove this member");
    }
  };
  const setRole = async (uid, role, branch = null) => {
    try {
      const params = new URLSearchParams({ role });
      if (branch) params.set("branch", branch);
      await api.post(`/groups/${groupId}/members/${uid}/role?${params.toString()}`);
      toast.success("Role updated");
      reload();
    } catch (err) {
      console.warn("Set role failed:", err);
      toast.error(formatApiError(err) || "Couldn't update role");
    }
  };

  const createInvite = async () => {
    try {
      await api.post(`/groups/${groupId}/invite`, { expires_in_days: 30 });
      toast.success("New invite code created");
      reload();
    } catch (err) {
      console.warn("Create invite failed:", err);
      toast.error(formatApiError(err) || "Couldn't create invite");
    }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        {isAdmin && pending.length > 0 && (
          <div className="legacy-card p-6" data-testid="pending-members">
            <h3 className="font-display text-xl text-forest mb-4">Pending requests ({pending.length})</h3>
            <ul className="space-y-3">
              {pending.map(m => (
                <li key={m.id} className="flex items-center gap-3 p-3 bg-cream rounded-lg" data-testid={`pending-${m.id}`}>
                  <MemberAvatar member={m} />
                  <div className="flex-1"><p className="font-medium">{m.name}</p><p className="text-sm text-sageGray">{m.email}</p></div>
                  <Button size="sm" onClick={() => approve(m.id)} className="bg-forest hover:bg-forestSoft text-ivory rounded-full" data-testid={`approve-${m.id}`}>Approve</Button>
                  <Button size="sm" variant="outline" onClick={() => reject(m.id)} className="border-forest/30 text-forest hover:bg-white rounded-full" data-testid={`reject-${m.id}`}>Reject</Button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="legacy-card p-6">
          <h3 className="font-display text-xl text-forest mb-4">Members ({members.filter(m => m.status === "verified").length})</h3>
          <ul className="grid sm:grid-cols-2 gap-3" data-testid="members-list">
            {members.filter(m => m.status === "verified").map(m => (
              <li key={m.id} className="flex items-center gap-3 p-3 bg-white border border-sage rounded-lg" data-testid={`member-${m.id}`}>
                <button
                  type="button"
                  onClick={() => setActiveMember(m)}
                  className="flex items-center gap-3 flex-1 min-w-0 text-left hover:bg-cream/60 rounded-lg p-1 -m-1 transition-colors"
                  data-testid={`open-member-${m.id}`}
                  title="View profile"
                >
                  <MemberAvatar member={m} />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{m.name}</p>
                    <p className="text-xs text-sageGray truncate">{m.email}</p>
                    {m.bio && <p className="text-[11px] text-charcoal/70 truncate mt-0.5 italic">{m.bio}</p>}
                    {m.on_map === false && (
                      <p className="text-[10px] uppercase tracking-widest text-gold mt-0.5" data-testid={`off-map-${m.id}`}>
                        Off map · awaiting verified relationship
                      </p>
                    )}
                  </div>
                </button>
                {m.role === "owner" && <Crown size={16} className="text-gold" weight="fill" />}
                {m.role === "admin" && <ShieldCheck size={16} className="text-forest" weight="fill" />}
                {m.role === "branch_leader" && (
                  <Badge className="bg-gold/15 text-forest border-0 rounded-full text-[10px]" title={`Branch: ${m.branch || "?"}`}>
                    <ShieldCheck size={11} className="mr-1" weight="fill" /> {m.branch}
                  </Badge>
                )}
                {isOwner && m.role !== "owner" && (
                  <Select
                    onValueChange={(v) => {
                      if (v === "branch_leader_paternal") setRole(m.id, "branch_leader", "paternal");
                      else if (v === "branch_leader_maternal") setRole(m.id, "branch_leader", "maternal");
                      else setRole(m.id, v);
                    }}
                    value={m.role === "branch_leader" ? `branch_leader_${m.branch}` : m.role}
                  >
                    <SelectTrigger className="w-[160px] h-8 text-xs bg-white border-sage" data-testid={`role-select-${m.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="member">Member</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="branch_leader_paternal">Branch leader · paternal</SelectItem>
                      <SelectItem value="branch_leader_maternal">Branch leader · maternal</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {isAdmin && (
        <div className="legacy-card p-6 h-fit space-y-5" data-testid="invite-panel">
          {isOwner && (
            <div className="pb-4 border-b border-sage" data-testid="auto-approve-panel">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <h4 className="font-display text-lg text-forest">Auto-approve joins</h4>
                  <p className="text-xs text-sageGray mt-0.5">Skip admin approval for invite-link joins. Members still need a verified relationship to show on the map.</p>
                </div>
                <Switch
                  checked={autoApprove}
                  onCheckedChange={toggleAutoApprove}
                  data-testid="auto-approve-toggle"
                  className="data-[state=checked]:bg-forest"
                />
              </div>
            </div>
          )}
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-xl text-forest">Invite codes</h3>
            <Button size="sm" onClick={createInvite} className="bg-forest hover:bg-forestSoft text-ivory rounded-full" data-testid="create-invite-button"><Plus size={14} className="mr-1" weight="bold" /> New</Button>
          </div>
          <p className="text-xs text-sageGray mb-4">Share an active code with a relative. {autoApprove ? "They'll join right away — but only show on the map after a verified relationship." : "They'll need your approval after joining."}</p>
          <ul className="space-y-2">
            {invites.map(inv => (
              <li key={inv.id} className="flex items-center gap-2 p-3 bg-cream border border-sage rounded-lg" data-testid={`invite-${inv.id}`}>
                <code className="font-mono text-forest tracking-widest text-sm flex-1 truncate">{inv.code}</code>
                <button
                  onClick={() => { navigator.clipboard.writeText(inv.code); toast.success("Code copied"); }}
                  className="text-sageGray hover:text-forest p-1.5 rounded-md hover:bg-white transition-colors"
                  title="Copy code"
                  data-testid={`copy-invite-${inv.code}`}
                ><Copy size={16} /></button>
                <button
                  onClick={() => {
                    const link = `${window.location.origin}/join/${inv.code}`;
                    navigator.clipboard.writeText(link);
                    toast.success("Invite link copied");
                  }}
                  className="text-forest hover:bg-white p-1.5 rounded-md transition-colors flex items-center gap-1.5 text-xs font-medium"
                  title="Copy invite link"
                  data-testid={`copy-link-${inv.code}`}
                ><LinkIcon size={14} weight="bold" /> link</button>
              </li>
            ))}
            {invites.length === 0 && <li className="text-sm text-sageGray">No active codes yet.</li>}
          </ul>
        </div>
      )}
      <MemberDetailDialog member={activeMember} open={!!activeMember} onOpenChange={(o) => !o && setActiveMember(null)} />
    </div>
  );
}

/* ---------- RELATIONSHIPS TAB ---------- */
function RelationshipsTab({ groupId, members, relationships, reload, currentUser }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ to_user_id: "", type: "parent", branch: "" });
  const verifiedMembers = members.filter(m => m.status === "verified" && m.id !== currentUser?.id);
  const memberById = useMemo(() => Object.fromEntries(members.map(m => [m.id, m])), [members]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/groups/${groupId}/relationships`, form);
      toast.success("Claim added — waiting for verification");
      setOpen(false); setForm({ to_user_id: "", type: "parent", branch: "" });
      reload();
    } catch (err) {
      console.warn("Add relationship failed:", err);
      toast.error(formatApiError(err) || "Couldn't save the relationship claim");
    }
  };

  const verify = async (rid) => {
    try {
      await api.post(`/groups/${groupId}/relationships/${rid}/verify`);
      toast.success("Verified");
      reload();
    } catch (err) {
      console.warn("Verify relationship failed:", err);
      toast.error(formatApiError(err) || "Couldn't verify this claim");
    }
  };
  const remove = async (rid) => {
    try {
      await api.delete(`/groups/${groupId}/relationships/${rid}`);
      toast.success("Removed");
      reload();
    } catch (err) {
      console.warn("Remove relationship failed:", err);
      toast.error(formatApiError(err) || "Couldn't remove this claim");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-display text-2xl text-forest">Relationship claims</h3>
          <p className="text-sm text-sageGray">Add who you are to others. Two members or an admin must verify each claim.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-forest hover:bg-forestSoft text-ivory rounded-full" data-testid="open-add-relationship-button"><Plus size={16} className="mr-1.5" weight="bold" /> Add relationship</Button>
          </DialogTrigger>
          <DialogContent className="bg-white">
            <DialogHeader><DialogTitle className="font-display text-2xl text-forest">Add a relationship</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4 pt-2" data-testid="add-relationship-form">
              <div>
                <Label className="text-forest">This person is my…</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger className="mt-1.5 bg-white border-sage" data-testid="rel-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {REL_TYPES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-forest">Member</Label>
                <Select value={form.to_user_id} onValueChange={(v) => setForm({ ...form, to_user_id: v })}>
                  <SelectTrigger className="mt-1.5 bg-white border-sage" data-testid="rel-member-select"><SelectValue placeholder="Choose member" /></SelectTrigger>
                  <SelectContent>
                    {verifiedMembers.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-forest">Family branch (optional)</Label>
                <Input value={form.branch} placeholder="Paternal / Maternal" onChange={(e) => setForm({ ...form, branch: e.target.value })} className="mt-1.5 bg-white border-sage focus-visible:ring-gold" data-testid="rel-branch-input" />
              </div>
              <Button type="submit" className="w-full bg-forest hover:bg-forestSoft text-ivory rounded-full" data-testid="rel-submit-button">Save claim</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="legacy-card overflow-hidden">
        <ul className="divide-y divide-sage" data-testid="relationships-list">
          {relationships.length === 0 && <li className="p-10 text-center text-sageGray">No relationships claimed yet.</li>}
          {relationships.map(r => {
            const from = memberById[r.from_user_id];
            const to = memberById[r.to_user_id];
            const canVerify = currentUser?.id !== r.from_user_id && !r.verified;
            const canRemove = currentUser?.id === r.from_user_id;
            return (
              <li key={r.id} className="p-4 flex items-center gap-4" data-testid={`rel-${r.id}`}>
                <MemberAvatar member={from} size={36} />
                <div className="flex-1 min-w-0 text-sm">
                  <p><span className="font-medium">{from?.name || "?"}</span> claims <span className="font-medium">{to?.name || "?"}</span> is their <span className="text-forest font-semibold">{REL_TYPES.find(t => t.value === r.type)?.label}</span>{r.branch ? ` · ${r.branch}` : ""}</p>
                  <p className="text-xs text-sageGray mt-0.5">{r.verifications?.length || 0} verification(s)</p>
                </div>
                {r.verified ? (
                  <Badge className="bg-forest/10 text-forest border-0 rounded-full"><CheckCircle size={12} className="mr-1" weight="fill" /> verified</Badge>
                ) : (
                  <Badge className="bg-gold/20 text-forest border-0 rounded-full"><Hourglass size={12} className="mr-1" weight="fill" /> pending</Badge>
                )}
                {canVerify && <Button size="sm" onClick={() => verify(r.id)} className="bg-forest hover:bg-forestSoft text-ivory rounded-full" data-testid={`verify-${r.id}`}>Verify</Button>}
                {canRemove && <Button size="sm" variant="outline" onClick={() => remove(r.id)} className="border-forest/30 text-forest hover:bg-cream rounded-full" data-testid={`remove-rel-${r.id}`}>Remove</Button>}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

/* ---------- ANNOUNCEMENTS TAB ---------- */
function AnnouncementsTab({ groupId, announcements, isAdmin, reload, members }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "" });
  const memberById = useMemo(() => Object.fromEntries(members.map(m => [m.id, m])), [members]);

  const post = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/groups/${groupId}/announcements`, form);
      toast.success("Announcement sent");
      setOpen(false); setForm({ title: "", body: "" });
      reload();
    } catch (err) {
      console.warn("Post announcement failed:", err);
      toast.error(formatApiError(err) || "Couldn't send announcement");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-display text-2xl text-forest">Announcements</h3>
          <p className="text-sm text-sageGray">A clean record of family-wide messages from admins.</p>
        </div>
        {isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-forest hover:bg-forestSoft text-ivory rounded-full" data-testid="open-announcement-button"><Megaphone size={16} className="mr-1.5" weight="fill" /> New announcement</Button>
            </DialogTrigger>
            <DialogContent className="bg-white">
              <DialogHeader><DialogTitle className="font-display text-2xl text-forest">Send announcement</DialogTitle></DialogHeader>
              <form onSubmit={post} className="space-y-4 pt-2" data-testid="announcement-form">
                <div>
                  <Label className="text-forest">Title</Label>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className="mt-1.5 bg-white border-sage focus-visible:ring-gold" data-testid="announcement-title-input" />
                </div>
                <div>
                  <Label className="text-forest">Message</Label>
                  <Textarea rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} required className="mt-1.5 bg-white border-sage focus-visible:ring-gold" data-testid="announcement-body-input" />
                </div>
                <Button type="submit" className="w-full bg-forest hover:bg-forestSoft text-ivory rounded-full" data-testid="announcement-submit-button">Send to circle</Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>
      <ul className="space-y-4" data-testid="announcements-list">
        {announcements.length === 0 && <li className="legacy-card p-8 text-center text-sageGray">No announcements yet.</li>}
        {announcements.map(a => (
          <li key={a.id} className="legacy-card p-6" data-testid={`announcement-${a.id}`}>
            <div className="flex items-start gap-4">
              <span className="w-10 h-10 rounded-full bg-forest text-gold flex items-center justify-center shrink-0">
                <Megaphone size={18} weight="duotone" />
              </span>
              <div className="flex-1 min-w-0">
                <h4 className="font-display text-xl text-forest">{a.title}</h4>
                <p className="text-xs uppercase tracking-widest text-sageGray mb-2">{memberById[a.author_id]?.name || "Admin"} · {new Date(a.created_at).toLocaleDateString()}</p>
                <p className="text-charcoal whitespace-pre-line leading-relaxed">{a.body}</p>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ---------- MESSAGES TAB ---------- */
function MessagesTab({ members, currentUser }) {
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const activeRef = useRef(null);
  const otherMembers = members.filter(m => m.status === "verified" && m.id !== currentUser?.id);

  // Keep activeRef in sync so the WS message handler always sees the current chat
  useEffect(() => { activeRef.current = active; }, [active]);

  // Open WebSocket once on mount, reconnect on close
  useEffect(() => {
    let stopped = false;
    let reconnectTimer = null;

    const open = () => {
      if (stopped) return;
      const token = getStoredToken();
      const base = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws") + "/api/ws";
      const wsUrl = token ? `${base}?token=${encodeURIComponent(token)}` : base;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => setConnected(true);
      ws.onclose = () => {
        setConnected(false);
        if (!stopped) reconnectTimer = setTimeout(open, 2000);
      };
      ws.onerror = (errEvt) => {
        console.warn("DM WebSocket error:", errEvt);
        try {
          ws.close();
        } catch (closeErr) {
          console.warn("DM WS close after error:", closeErr);
        }
      };
      ws.onmessage = (ev) => {
        try {
          const payload = JSON.parse(ev.data);
          if (payload.type !== "message") return;
          const m = payload.data;
          const cur = activeRef.current;
          if (!cur) return;
          const isThisThread =
            (m.from_user_id === cur.id && m.to_user_id === currentUser?.id) ||
            (m.to_user_id === cur.id && m.from_user_id === currentUser?.id);
          if (isThisThread) {
            setMessages(prev => prev.some(x => x.id === m.id) ? prev : [...prev, m]);
          }
        } catch (parseErr) {
          console.warn("Bad DM frame:", parseErr);
        }
      };
    };

    open();
    return () => {
      stopped = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try {
        wsRef.current?.close();
      } catch (closeErr) {
        console.warn("DM WS unmount close:", closeErr);
      }
    };
  }, [currentUser?.id]);

  const loadConversation = async (otherId) => {
    try {
      const { data } = await api.get(`/messages/conversation/${otherId}`);
      setMessages(data);
    } catch (err) {
      console.warn("Failed to load conversation:", err);
      toast.error(formatApiError(err) || "Couldn't load messages");
    }
  };

  const openChat = async (m) => {
    setActive(m);
    await loadConversation(m.id);
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !active) return;
    try {
      await api.post("/messages", { to_user_id: active.id, content: text.trim() });
      // WebSocket will echo the message back to us; no manual append needed.
      setText("");
    } catch (err) {
      console.warn("Failed to send message:", err);
      toast.error(formatApiError(err) || "Couldn't send message");
    }
  };

  return (
    <div className="grid md:grid-cols-3 gap-6 h-[600px]">
      <div className="legacy-card p-4 overflow-y-auto" data-testid="conversations-list">
        <div className="flex items-center justify-between mb-3 px-2">
          <h3 className="font-display text-xl text-forest">Direct messages</h3>
          <span className={`text-[10px] uppercase tracking-widest ${connected ? "text-forest" : "text-sageGray"}`} data-testid="ws-status">
            {connected ? "● live" : "○ reconnecting"}
          </span>
        </div>
        <ul className="space-y-1">
          {otherMembers.length === 0 && <li className="px-2 text-sm text-sageGray">No other verified members yet.</li>}
          {otherMembers.map(m => (
            <li key={m.id}>
              <button
                onClick={() => openChat(m)}
                className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${active?.id === m.id ? "bg-forest text-ivory" : "hover:bg-cream"}`}
                data-testid={`chat-with-${m.id}`}
              >
                <MemberAvatar member={m} size={36} />
                <span className="truncate font-medium">{m.name}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
      <div className="md:col-span-2 legacy-card flex flex-col">
        {!active ? (
          <div className="flex-1 flex items-center justify-center text-sageGray">Select a family member to start chatting</div>
        ) : (
          <>
            <header className="p-4 border-b border-sage flex items-center gap-3">
              <MemberAvatar member={active} size={40} />
              <div><p className="font-medium">{active.name}</p><p className="text-xs text-sageGray">{active.email}</p></div>
            </header>
            <ScrollArea className="flex-1 p-4">
              <ul className="space-y-2" data-testid="message-list">
                {messages.map(m => (
                  <li key={m.id} className={`flex ${m.from_user_id === currentUser?.id ? "justify-end" : "justify-start"}`} data-testid={`message-${m.id}`}>
                    <div className={`max-w-[78%] px-4 py-2 rounded-2xl ${m.from_user_id === currentUser?.id ? "bg-forest text-ivory rounded-br-sm" : "bg-cream text-charcoal rounded-bl-sm"}`}>
                      <p className="text-sm whitespace-pre-line">{m.content}</p>
                      <p className={`text-[10px] mt-1 ${m.from_user_id === currentUser?.id ? "text-ivory/70" : "text-sageGray"}`}>{new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </ScrollArea>
            <form onSubmit={send} className="p-3 border-t border-sage flex gap-2" data-testid="send-message-form">
              <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a message…" className="bg-white border-sage focus-visible:ring-gold" data-testid="message-input" />
              <Button type="submit" className="bg-forest hover:bg-forestSoft text-ivory rounded-full px-6" data-testid="send-message-button">Send</Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

/* ---------- PHOTOS TAB ---------- */
function PhotosTab({ groupId, photos, members, reload }) {
  const [eventName, setEventName] = useState("");
  const [caption, setCaption] = useState("");
  const memberById = useMemo(() => Object.fromEntries(members.map(m => [m.id, m])), [members]);

  const handleUploaded = async (path) => {
    try {
      await api.post(`/groups/${groupId}/photos`, { storage_path: path, caption, event_name: eventName, tagged_user_ids: [] });
      toast.success("Photo added");
      setEventName(""); setCaption("");
      reload();
    } catch (err) {
      console.warn("Failed to save photo:", err);
      toast.error(formatApiError(err) || "Couldn't save photo");
    }
  };

  return (
    <div className="space-y-6">
      <div className="legacy-card p-6 flex flex-col md:flex-row gap-3 md:items-end" data-testid="photo-upload-panel">
        <div className="flex-1">
          <Label className="text-forest">Event name (optional)</Label>
          <Input value={eventName} onChange={(e) => setEventName(e.target.value)} placeholder="e.g. Diwali 2025" className="mt-1.5 bg-white border-sage focus-visible:ring-gold" data-testid="photo-event-input" />
        </div>
        <div className="flex-1">
          <Label className="text-forest">Caption</Label>
          <Input value={caption} onChange={(e) => setCaption(e.target.value)} className="mt-1.5 bg-white border-sage focus-visible:ring-gold" data-testid="photo-caption-input" />
        </div>
        <PhotoUpload onUploaded={handleUploaded} label="Add photo" testId="group-photo-upload" />
      </div>

      {photos.length === 0 ? (
        <div className="legacy-card p-10 text-center text-sageGray" data-testid="empty-photos">No photos yet. Add the first one.</div>
      ) : (
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4" data-testid="photos-grid">
          {photos.map(p => (
            <figure key={p.id} className="legacy-card overflow-hidden" data-testid={`photo-${p.id}`}>
              <div className="aspect-square bg-cream overflow-hidden">
                <img src={fileUrl(p.storage_path)} alt={p.caption || p.event_name} className="w-full h-full object-cover" />
              </div>
              <figcaption className="p-4">
                {p.event_name && <p className="text-xs uppercase tracking-widest text-gold mb-1">{p.event_name}</p>}
                {p.caption && <p className="text-charcoal">{p.caption}</p>}
                <p className="text-xs text-sageGray mt-1">By {memberById[p.owner_id]?.name || "—"} · {new Date(p.created_at).toLocaleDateString()}</p>
              </figcaption>
            </figure>
          ))}
        </div>
      )}
    </div>
  );
}


/* ---------- MEMORIALS TAB ---------- */
function MemorialsTab({ groupId, memorials, members, currentUser, reload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", birth_year: "", death_year: "", bio: "", branch: "", photo_path: null, personal_photo_path: null });
  const [active, setActive] = useState(null); // currently selected memorial detail
  const [tributes, setTributes] = useState([]);
  const [tributeText, setTributeText] = useState("");
  const [editing, setEditing] = useState(null); // memorial being edited
  const [editForm, setEditForm] = useState({ name: "", birth_year: "", death_year: "", bio: "", branch: "", photo_path: null, personal_photo_path: null });
  const memberById = useMemo(() => Object.fromEntries(members.map(m => [m.id, m])), [members]);

  const openEdit = (m) => {
    setEditing(m);
    setEditForm({
      name: m.name || "",
      birth_year: m.birth_year || "",
      death_year: m.death_year || "",
      bio: m.bio || "",
      branch: m.branch || "",
      photo_path: m.photo_path || null,
      personal_photo_path: m.personal_photo_path || null,
    });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editing) return;
    try {
      await api.patch(`/groups/${groupId}/memorials/${editing.id}`, {
        name: editForm.name.trim(),
        birth_year: editForm.birth_year ? Number(editForm.birth_year) : null,
        death_year: editForm.death_year ? Number(editForm.death_year) : null,
        bio: editForm.bio,
        branch: editForm.branch || null,
        photo_path: editForm.photo_path,
        personal_photo_path: editForm.personal_photo_path,
      });
      toast.success("Memorial updated");
      setEditing(null);
      reload();
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't save changes");
    }
  };

  const openDetail = async (m) => {
    setActive(m);
    try {
      const { data } = await api.get(`/groups/${groupId}/memorials/${m.id}/tributes`);
      setTributes(data);
    } catch (err) {
      console.warn("Load tributes failed:", err);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/groups/${groupId}/memorials`, {
        name: form.name.trim(),
        birth_year: form.birth_year ? Number(form.birth_year) : null,
        death_year: form.death_year ? Number(form.death_year) : null,
        bio: form.bio,
        branch: form.branch || null,
        photo_path: form.photo_path,
        personal_photo_path: form.personal_photo_path,
      });
      toast.success("Memorial created — awaiting confirmations");
      setOpen(false);
      setForm({ name: "", birth_year: "", death_year: "", bio: "", branch: "", photo_path: null, personal_photo_path: null });
      reload();
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't save memorial");
    }
  };

  const confirm = async (m) => {
    try {
      const { data } = await api.post(`/groups/${groupId}/memorials/${m.id}/confirm`);
      toast.success(data.confirmed ? "Memorial confirmed" : `Confirmed (${data.count} total)`);
      reload();
      if (active?.id === m.id) setActive({ ...m, confirmations: [...(m.confirmations || []), currentUser?.id] });
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't confirm");
    }
  };

  const addTribute = async (e) => {
    e.preventDefault();
    if (!tributeText.trim() || !active) return;
    try {
      const { data } = await api.post(`/groups/${groupId}/memorials/${active.id}/tributes`, { content: tributeText.trim() });
      setTributes(prev => [data, ...prev]);
      setTributeText("");
      toast.success("Tribute added");
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't add tribute");
    }
  };

  const branchColor = (b) => {
    if (b === "paternal") return "border-gold/60 bg-gold/5";
    if (b === "maternal") return "border-forest/30 bg-forest/5";
    return "border-sage";
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-display text-2xl text-forest">In memoriam</h3>
          <p className="text-sm text-sageGray">Honor relatives no longer with us. Confirmed memorials appear on the family map with a silver ring.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-forest hover:bg-forestSoft text-ivory rounded-full" data-testid="open-memorial-button"><Flower size={16} className="mr-1.5" weight="duotone" /> Add memorial</Button>
          </DialogTrigger>
          <DialogContent className="bg-white max-w-lg">
            <DialogHeader><DialogTitle className="font-display text-2xl text-forest">Add a memorial</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4 pt-2" data-testid="memorial-form">
              <div>
                <Label className="text-forest">Full name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1.5 bg-white border-sage focus-visible:ring-gold" data-testid="memorial-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-forest">Born</Label>
                  <Input type="number" value={form.birth_year} onChange={(e) => setForm({ ...form, birth_year: e.target.value })} placeholder="1925" className="mt-1.5 bg-white border-sage focus-visible:ring-gold" data-testid="memorial-birth-input" />
                </div>
                <div>
                  <Label className="text-forest">Passed</Label>
                  <Input type="number" value={form.death_year} onChange={(e) => setForm({ ...form, death_year: e.target.value })} placeholder="1998" className="mt-1.5 bg-white border-sage focus-visible:ring-gold" data-testid="memorial-death-input" />
                </div>
              </div>
              <div>
                <Label className="text-forest">Family branch</Label>
                <Select value={form.branch} onValueChange={(v) => setForm({ ...form, branch: v })}>
                  <SelectTrigger className="mt-1.5 bg-white border-sage" data-testid="memorial-branch-select"><SelectValue placeholder="Choose a side" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="paternal">Paternal side</SelectItem>
                    <SelectItem value="maternal">Maternal side</SelectItem>
                    <SelectItem value="both">Both / not sure</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-forest">A short bio</Label>
                <Textarea rows={4} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="mt-1.5 bg-white border-sage focus-visible:ring-gold" placeholder="A note about who they were…" data-testid="memorial-bio-input" />
              </div>
              <PhotoUpload onUploaded={(p) => setForm({ ...form, photo_path: p })} label={form.photo_path ? "Obituary photo added" : "Add obituary photo (formal)"} testId="memorial-photo-upload" />
              <PhotoUpload onUploaded={(p) => setForm({ ...form, personal_photo_path: p })} label={form.personal_photo_path ? "Personal photo added" : "Add personal photo (everyday)"} testId="memorial-personal-photo-upload" />
              <Button type="submit" className="w-full bg-forest hover:bg-forestSoft text-ivory rounded-full" data-testid="memorial-submit-button">Create memorial</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {memorials.length === 0 ? (
        <div className="legacy-card p-10 text-center text-sageGray" data-testid="empty-memorials">No memorials yet. Honor someone with a profile that lives on in the family map.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="memorials-grid">
          {memorials.map(m => {
            const confirmCount = (m.confirmations || []).length;
            const hasConfirmed = (m.confirmations || []).includes(currentUser?.id);
            const isCreator = m.created_by === currentUser?.id;
            return (
              <article key={m.id} className={`legacy-card overflow-hidden ${branchColor(m.branch)} border-2 relative`} data-testid={`memorial-card-${m.id}`}>
                {m.can_edit && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); openEdit(m); }}
                    className="absolute top-2 right-2 z-10 w-9 h-9 rounded-full bg-white/95 border border-sage text-forest hover:bg-cream shadow-sm flex items-center justify-center transition-colors"
                    title="Edit memorial"
                    data-testid={`memorial-edit-${m.id}`}
                  >
                    <PencilSimple size={16} weight="bold" />
                  </button>
                )}
                <button onClick={() => openDetail(m)} className="w-full text-left">
                  <div className="aspect-square bg-cream overflow-hidden flex items-center justify-center">
                    {m.photo_path ? (
                      <img src={fileUrl(m.photo_path)} alt={m.name} className="w-full h-full object-cover" />
                    ) : (
                      <Flower size={48} weight="duotone" className="text-sageGray" />
                    )}
                  </div>
                  <div className="p-5">
                    <h4 className="font-display text-xl text-forest">{m.name}</h4>
                    <p className="text-xs uppercase tracking-widest text-sageGray mt-0.5">
                      {m.birth_year || "—"} – {m.death_year || "—"} {m.branch ? `· ${m.branch}` : ""}
                    </p>
                    {m.bio && <p className="text-sm text-charcoal mt-3 line-clamp-2">{m.bio}</p>}
                  </div>
                </button>
                <footer className="px-5 pb-5 flex items-center justify-between border-t border-sage pt-3">
                  <Badge className={`${m.status === "confirmed" ? "bg-forest/10 text-forest" : "bg-gold/20 text-forest"} border-0 rounded-full`}>
                    {m.status === "confirmed" ? <><CheckCircle size={12} className="mr-1" weight="fill" /> confirmed</> : <><Hourglass size={12} className="mr-1" weight="fill" /> {confirmCount}/5 confirmations</>}
                  </Badge>
                  {!isCreator && !hasConfirmed && m.status !== "confirmed" && (
                    <Button size="sm" onClick={() => confirm(m)} className="bg-forest hover:bg-forestSoft text-ivory rounded-full" data-testid={`memorial-confirm-${m.id}`}>I knew them</Button>
                  )}
                </footer>
              </article>
            );
          })}
        </div>
      )}

      <Dialog open={!!active} onOpenChange={(o) => { if (!o) { setActive(null); setTributes([]); } }}>
        <DialogContent className="bg-white max-w-2xl">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-3xl text-forest">{active.name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-5 pt-2 max-h-[70vh] overflow-y-auto pr-2">
                <div className="flex gap-4">
                  {active.photo_path && (
                    <div className="text-center" data-testid="memorial-obituary-photo">
                      <img src={fileUrl(active.photo_path)} alt={`${active.name} obituary`} className="w-32 h-32 rounded-xl object-cover border border-sage" />
                      <p className="text-[10px] uppercase tracking-widest text-sageGray mt-1">Obituary</p>
                    </div>
                  )}
                  {active.personal_photo_path && (
                    <div className="text-center" data-testid="memorial-personal-photo">
                      <img src={fileUrl(active.personal_photo_path)} alt={`${active.name} personal`} className="w-32 h-32 rounded-xl object-cover border border-sage" />
                      <p className="text-[10px] uppercase tracking-widest text-sageGray mt-1">Personal</p>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs uppercase tracking-widest text-sageGray">{active.birth_year || "—"} – {active.death_year || "—"}{active.branch ? ` · ${active.branch} side` : ""}</p>
                    {active.bio && <p className="text-charcoal mt-2 whitespace-pre-line">{active.bio}</p>}
                  </div>
                </div>
                <div className="gold-divider" />
                <div>
                  <h4 className="font-display text-xl text-forest mb-3">Tributes</h4>
                  <form onSubmit={addTribute} className="flex gap-2 mb-4" data-testid="add-tribute-form">
                    <Input value={tributeText} onChange={(e) => setTributeText(e.target.value)} placeholder="Share a memory…" className="bg-white border-sage focus-visible:ring-gold" data-testid="tribute-input" />
                    <Button type="submit" className="bg-forest hover:bg-forestSoft text-ivory rounded-full px-5" data-testid="tribute-submit">Post</Button>
                  </form>
                  <ul className="space-y-3" data-testid="tribute-list">
                    {tributes.length === 0 && <li className="text-sm text-sageGray">No tributes yet. Be the first to share a memory.</li>}
                    {tributes.map(t => (
                      <li key={t.id} className="bg-cream rounded-lg p-3 border border-sage" data-testid={`tribute-${t.id}`}>
                        <p className="text-charcoal whitespace-pre-line">{t.content}</p>
                        <p className="text-xs text-sageGray mt-1.5">{t.author_name || memberById[t.author_id]?.name || "—"} · {new Date(t.created_at).toLocaleDateString()}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ---------- EDIT MEMORIAL ---------- */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-white max-w-lg" data-testid="edit-memorial-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-forest">Edit memorial</DialogTitle>
          </DialogHeader>
          {editing?.edit_reason && (
            <p className="text-xs uppercase tracking-widest text-gold/90 mt-1" data-testid="memorial-edit-reason">
              You can edit this because you are the {editing.edit_reason}.
            </p>
          )}
          <form onSubmit={saveEdit} className="space-y-4 pt-2" data-testid="edit-memorial-form">
            <div>
              <Label className="text-forest">Full name</Label>
              <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} required className="mt-1.5 bg-white border-sage focus-visible:ring-gold" data-testid="edit-memorial-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-forest">Born</Label>
                <Input type="number" value={editForm.birth_year} onChange={(e) => setEditForm({ ...editForm, birth_year: e.target.value })} placeholder="1918" className="mt-1.5 bg-white border-sage focus-visible:ring-gold" data-testid="edit-memorial-birth" />
              </div>
              <div>
                <Label className="text-forest">Died</Label>
                <Input type="number" value={editForm.death_year} onChange={(e) => setEditForm({ ...editForm, death_year: e.target.value })} placeholder="2002" className="mt-1.5 bg-white border-sage focus-visible:ring-gold" data-testid="edit-memorial-death" />
              </div>
            </div>
            <div>
              <Label className="text-forest">Family branch</Label>
              <Select value={editForm.branch} onValueChange={(v) => setEditForm({ ...editForm, branch: v })}>
                <SelectTrigger className="mt-1.5 bg-white border-sage" data-testid="edit-memorial-branch"><SelectValue placeholder="Choose a side" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="paternal">Paternal side</SelectItem>
                  <SelectItem value="maternal">Maternal side</SelectItem>
                  <SelectItem value="both">Both / not sure</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-forest">Biography</Label>
              <Textarea rows={4} value={editForm.bio} onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })} className="mt-1.5 bg-white border-sage focus-visible:ring-gold" data-testid="edit-memorial-bio" />
            </div>
            <PhotoUpload onUploaded={(p) => setEditForm({ ...editForm, photo_path: p })} label={editForm.photo_path ? "Change obituary photo" : "Add obituary photo (formal)"} testId="edit-memorial-photo" />
            <PhotoUpload onUploaded={(p) => setEditForm({ ...editForm, personal_photo_path: p })} label={editForm.personal_photo_path ? "Change personal photo" : "Add personal photo (everyday)"} testId="edit-memorial-personal-photo" />
            <div className="flex items-center justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={() => setEditing(null)} className="text-sageGray hover:bg-cream rounded-full" data-testid="edit-memorial-cancel">Cancel</Button>
              <Button type="submit" className="bg-forest hover:bg-forestSoft text-ivory rounded-full px-6" data-testid="edit-memorial-save">Save changes</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/* ---------- DOCUMENTS TAB (Fix 11) ---------- */
function DocumentsTab({ groupId, currentUser, isAdmin }) {
  const [docs, setDocs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", note: "", file_path: "", content_type: "", size: 0, original_filename: "" });
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const token = getStoredToken();

  const load = async () => {
    try {
      const { data } = await api.get(`/groups/${groupId}/documents`);
      setDocs(data);
    } catch (err) {
      console.warn("Load docs failed:", err);
    }
  };
  useEffect(() => { load(); }, [groupId]); // eslint-disable-line react-hooks/exhaustive-deps

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File is too large. Max size is 10 MB.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload-document", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({
        ...f,
        file_path: data.storage_path,
        content_type: data.content_type || file.type,
        size: data.size || file.size,
        original_filename: data.original_filename || file.name,
        name: f.name || file.name.replace(/\.[^.]+$/, ""),
      }));
      toast.success("File uploaded — add a name and save.");
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't upload that file");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.file_path) {
      toast.error("Pick a file first");
      return;
    }
    if (!form.name.trim()) {
      toast.error("Give this document a name");
      return;
    }
    try {
      await api.post(`/groups/${groupId}/documents`, {
        name: form.name.trim(),
        note: form.note.trim(),
        file_path: form.file_path,
        content_type: form.content_type,
        size: form.size,
      });
      toast.success("Document saved");
      setForm({ name: "", note: "", file_path: "", content_type: "", size: 0, original_filename: "" });
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't save document");
    }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Delete "${doc.name}"? This can't be undone.`)) return;
    try {
      await api.delete(`/groups/${groupId}/documents/${doc.id}`);
      toast.success("Document removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't delete that document");
    }
  };

  const fileHref = (doc) => {
    const base = `${process.env.REACT_APP_BACKEND_URL}/api/files/${doc.file_path}`;
    return token ? `${base}?auth=${encodeURIComponent(token)}` : base;
  };

  const formatSize = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-display text-2xl text-forest">Family documents</h3>
          <p className="text-sm text-sageGray">Reference files for the circle — wills, certificates, photos of records. PDF or image, up to 10 MB.</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setForm({ name: "", note: "", file_path: "", content_type: "", size: 0, original_filename: "" }); }}>
          <DialogTrigger asChild>
            <Button className="bg-forest hover:bg-forestSoft text-ivory rounded-full" data-testid="open-document-upload"><Plus size={16} className="mr-1.5" weight="bold" /> Add document</Button>
          </DialogTrigger>
          <DialogContent className="bg-white max-w-lg" data-testid="document-upload-dialog">
            <DialogHeader><DialogTitle className="font-display text-2xl text-forest">Add a document</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4 pt-2" data-testid="document-upload-form">
              <div>
                <Label className="text-forest">Pick a file</Label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                  onChange={onPickFile}
                  disabled={uploading}
                  className="mt-1.5 block w-full text-sm text-forest file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-medium file:bg-forest file:text-ivory hover:file:bg-forestSoft"
                  data-testid="document-file-input"
                />
                {uploading && <p className="text-xs text-sageGray mt-2">Uploading…</p>}
                {form.original_filename && !uploading && (
                  <p className="text-xs text-forest mt-2 truncate" data-testid="document-uploaded-name">Selected: {form.original_filename} ({formatSize(form.size)})</p>
                )}
              </div>
              <div>
                <Label className="text-forest">Document name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Grandma's marriage certificate" className="mt-1.5 bg-white border-sage focus-visible:ring-gold" data-testid="document-name-input" />
              </div>
              <div>
                <Label className="text-forest">Note (optional)</Label>
                <Textarea rows={3} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1.5 bg-white border-sage focus-visible:ring-gold" placeholder="Where it came from, who can confirm it…" data-testid="document-note-input" />
              </div>
              <Button type="submit" disabled={!form.file_path || uploading} className="w-full bg-forest hover:bg-forestSoft text-ivory rounded-full" data-testid="document-submit-button">Save document</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      {docs.length === 0 ? (
        <div className="legacy-card p-10 text-center text-sageGray" data-testid="empty-documents">No documents yet. Upload PDFs, certificates, or scans for everyone in this circle.</div>
      ) : (
        <ul className="grid sm:grid-cols-2 gap-4" data-testid="documents-list">
          {docs.map((d) => {
            const canDelete = isAdmin || d.uploaded_by === currentUser?.id;
            return (
              <li key={d.id} className="legacy-card p-5 flex items-start gap-3" data-testid={`document-${d.id}`}>
                <span className="w-11 h-11 shrink-0 rounded-lg bg-forest/10 text-forest flex items-center justify-center">
                  <File size={22} weight="duotone" />
                </span>
                <div className="flex-1 min-w-0">
                  <a href={fileHref(d)} target="_blank" rel="noreferrer" className="font-medium text-forest hover:underline truncate block" data-testid={`document-link-${d.id}`}>{d.name}</a>
                  <p className="text-xs text-sageGray mt-0.5">
                    Added by {d.uploaded_by_name || "—"} · {new Date(d.created_at).toLocaleDateString()} {d.size ? `· ${formatSize(d.size)}` : ""}
                  </p>
                  {d.note && <p className="text-sm text-charcoal mt-2 whitespace-pre-line">{d.note}</p>}
                </div>
                <div className="flex flex-col items-end gap-1.5">
                  <a href={fileHref(d)} target="_blank" rel="noreferrer" className="text-forest hover:text-forestSoft p-1.5" title="Open" data-testid={`document-download-${d.id}`}>
                    <DownloadSimple size={16} weight="bold" />
                  </a>
                  {canDelete && (
                    <button onClick={() => remove(d)} className="text-red-700 hover:text-red-800 p-1.5" title="Delete" data-testid={`document-delete-${d.id}`}>
                      <Trash size={16} weight="bold" />
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
